'use client'

import { createContext, type ReactNode, useContext, useMemo } from 'react'

import { GALLERY_DISPLAY_DEFAULT, type GalleryDisplayType } from '@/defs/gallery'
import { usePriceSources } from '@/hooks/usePriceSources'
import { useReduxSelector } from '@/store/hooks'
import { selectors } from '@/store/selectors'
import { getGalleryDisplay } from '@/utils/galleryUtil'

import { useGalleryKey } from '../hooks/useGalleryKey'

type GalleryDisplayProviderType = GalleryDisplayType

const DEFAULT_DISPLAY_PROVIDER: GalleryDisplayProviderType = {
  ...GALLERY_DISPLAY_DEFAULT,
}

const GalleryDisplayContext = createContext<GalleryDisplayProviderType>(DEFAULT_DISPLAY_PROVIDER)

type Props = Partial<GalleryDisplayProviderType> & {
  children: ReactNode
}

export const GalleryDisplayProvider = ({
  children,
  groupby,
  groupbySlugs,
  size,
  sortby,
  sortbyDefaultIcon,
  sortbyDefaultLabel,
  sortbyDirection,
  sortbyOptions,
  stats,
  statsRoles,
  view,
}: Props) => {
  useGalleryKey()
  const prefs = useReduxSelector(selectors.galleryPrefs)
  const priceSources = usePriceSources()

  const value = useMemo<GalleryDisplayProviderType>(
    () =>
      getGalleryDisplay({
        config: {
          groupby,
          groupbySlugs,
          size,
          sortby,
          sortbyDefaultIcon,
          sortbyDefaultLabel,
          sortbyDirection,
          sortbyOptions,
          stats,
          statsRoles,
          view,
        },
        prefs,
        priceSources,
      }),
    [
      groupby,
      groupbySlugs,
      priceSources,
      prefs,
      size,
      sortby,
      sortbyDefaultIcon,
      sortbyDefaultLabel,
      sortbyDirection,
      sortbyOptions,
      stats,
      statsRoles,
      view,
    ],
  )

  return <GalleryDisplayContext.Provider value={value}>{children}</GalleryDisplayContext.Provider>
}

export const useGalleryDisplay = (): GalleryDisplayProviderType => useContext(GalleryDisplayContext)
